import { styled } from '@linaria/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  MeasureDataByEntity,
  MeasureDataResponse,
} from '../../../../../api/work-periods-client/work-periods-client.type';
import { useGlobalStore } from '../../../../../store/global-store/global-store';
import { useProcessAnalysisStore } from '../../../../../store/process-analysis-store/process-analysis-store';
import { useSelectedCapacityTeams } from '../../../../../store/process-analysis-store/process-analysis-store.hooks';
import {
  baseWhite,
  fontFamily,
  fontSizeSmall,
  fontWeightBold,
  fontWeightMedium,
  inkDarker,
  inkDarkest,
  secondaryBase,
  skyDark,
  skyLight,
  skyLightest,
} from '../../../../../styles/design-tokens';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Tab } from '../../../process-analysis.type';
import { customCapacitySort, getCapacityTableColumns, transformCapacityData } from './table.helpers';
import type { CapacitySortColumn } from './table.type';

type Props = {
  portfolioData: MeasureDataResponse | undefined;
  projectData: MeasureDataByEntity | undefined;
};

export const CapacityTable = ({ portfolioData, projectData }: Props) => {
  const { t } = useTranslation();
  const portfolio = useGlobalStore((state) => state.portfolio);
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const selectedCapacityTeams = useSelectedCapacityTeams();
  const selectedTeams = useProcessAnalysisStore((state) => state.selectedTeams);
  const [sortedBy, setSortedBy] = useState<CapacitySortColumn | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  const projectIdToName = portfolio?.projects.map((project) => ({ id: project.id, name: project.name }));
  const columns = getCapacityTableColumns(t);

  const setSorting = (field: CapacitySortColumn) => {
    const reversed = field === sortedBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortedBy(field);
  };

  const selection = activeTab === Tab.Portfolios ? selectedCapacityTeams : selectedTeams;
  const tableData = transformCapacityData(projectData, portfolioData, selection, projectIdToName);

  const sortedData = sortedBy
    ? customCapacitySort(tableData, sortedBy, reverseSortDirection ? 'desc' : 'asc')
    : tableData;

  return (
    <TableContainer>
      <TableHeader>
        {columns.map((column) => (
          <HeaderCell key={column.key.toString()} onClick={() => setSorting(column.key as CapacitySortColumn)}>
            <HeaderContent>
              <HeaderText>{column.label}</HeaderText>
              {column.sortable !== false && (
                <Icon
                  name={sortedBy === column.key ? (reverseSortDirection ? 'south' : 'north') : 'south'}
                  color={sortedBy === column.key ? secondaryBase : skyDark}
                  size={16}
                  style={{
                    cursor: 'pointer',
                    opacity: sortedBy === column.key ? 1 : 0.5,
                  }}
                />
              )}
            </HeaderContent>
          </HeaderCell>
        ))}
      </TableHeader>
      <TableBody>
        {sortedData.map((row) => (
          <TableRow key={row.projectId}>
            {columns.map((column) => (
              <DataCell key={column.key.toString()}>
                {column.render ? column.render(row[column.key], row) : row[column.key]?.toString()}
              </DataCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </TableContainer>
  );
};

const TableContainer = styled.div`
  background-color: ${baseWhite};
  border-radius: 0;
  overflow: auto;
  height: 100%;
`;

const TableHeader = styled.div`
  display: flex;
  background-color: ${baseWhite};
`;

const HeaderCell = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px 12px 30px;
  border: 1px solid ${skyLight};
  border-bottom: 1px solid ${skyLight};
  flex: 1;
  cursor: pointer;
  
  &:first-child {
    border-left: 1px solid ${skyLight};
  }
  
  &:not(:first-child) {
    border-left: none;
  }
  
  &:last-child {
    border-right: 1px solid ${skyLight};
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 10px;
`;

const HeaderText = styled.span`
  font-family: ${fontFamily}, sans-serif;
  font-weight: ${fontWeightBold};
  font-size: ${fontSizeSmall};
  line-height: 1em;
  color: ${inkDarker};
  text-align: left;
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
`;

const TableRow = styled.div`
  display: flex;
  background-color: ${baseWhite};
  
  &:hover {
    background-color: ${skyLightest};
  }
`;

const DataCell = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 30px;
  border-bottom: 1px solid ${skyLight};
  border-right: 1px solid ${skyLight};
  flex: 1;
  font-family: ${fontFamily}, sans-serif;
  font-weight: ${fontWeightMedium};
  font-size: ${fontSizeSmall};
  line-height: 1.4285714285714286em;
  color: ${inkDarkest};
  text-align: left;
  
  &:first-child {
    border-left: 1px solid ${skyLight};
  }
  
  &:last-child {
    border-right: 1px solid ${skyLight};
  }
`;
